/* eslint-disable react/no-array-index-key */
import React from 'react';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import CopyAllTwoToneIcon from '@mui/icons-material/CopyAllTwoTone';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import FileUploadTwoToneIcon from '@mui/icons-material/FileUploadTwoTone';
import FileDownloadTwoToneIcon from '@mui/icons-material/FileDownloadTwoTone';
import AccessTimeTwoToneIcon from '@mui/icons-material/AccessTimeTwoTone';
import ForkRightTwoToneIcon from '@mui/icons-material/ForkRightTwoTone';
import PictureAsPdfTwoToneIcon from '@mui/icons-material/PictureAsPdfTwoTone';
import VisibilityTwoToneIcon from '@mui/icons-material/VisibilityTwoTone';
import ContentCopyTwoToneIcon from '@mui/icons-material/ContentCopyTwoTone';
import BlockTwoToneIcon from '@mui/icons-material/BlockTwoTone';
import RefreshIcon from '@mui/icons-material/Refresh';
import AdsClickIcon from '@mui/icons-material/AdsClick';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import Button from '../button';
import { OptionItem } from '../cards/card.types';
import {
  type EnhancedOptionItem,
  getOptionItem,
  getMenuItemProps,
  divideOptionsIntoSections,
} from './features/menu-options';

export type MouseEventLI = React.MouseEvent<HTMLLIElement>;

export type SelectedItem = { option: OptionItem, item: Record<string, unknown> }

interface MenuOptionsProps {
  readonly options: EnhancedOptionItem[];
  readonly item: Record<string, unknown>;
  readonly onOptionClick: (e: MouseEventLI, selected: SelectedItem, anchor: HTMLButtonElement | null) => void;
  readonly children?: JSX.Element;
  readonly vertical?: boolean;
}

export function renderIcon(option: OptionItem) {
  const OptionIconMap = {
    [OptionItem.Edit]: EditTwoToneIcon,
    [OptionItem.Duplicate]: CopyAllTwoToneIcon,
    [OptionItem.Rename]: EditTwoToneIcon,
    [OptionItem.Delete]: DeleteTwoToneIcon,
    [OptionItem.Regenerate]: RefreshIcon,
    [OptionItem.Test]: AdsClickIcon,
    [OptionItem.Export]: FileUploadTwoToneIcon,
    [OptionItem.Import]: FileDownloadTwoToneIcon,
    [OptionItem.Activity]: AccessTimeTwoToneIcon,
    [OptionItem.Workflows]: ForkRightTwoToneIcon,
    [OptionItem.Reports]: PictureAsPdfTwoToneIcon,
    [OptionItem.Shared]: VisibilityTwoToneIcon,
    [OptionItem.Repeatables]: ContentCopyTwoToneIcon,
    [OptionItem.Deactivate]: BlockTwoToneIcon,
  };

  const Icon = OptionIconMap[option];
  return <Icon />;
}

export default function MenuOptions({ item, ...props }: MenuOptionsProps) {
  const [anchor, setAnchor] = React.useState<HTMLButtonElement | null>(null);
  const sectionedOptions = divideOptionsIntoSections(props.options);

  function handleOptionClick(e: MouseEventLI, clickedItem: SelectedItem) {
    props.onOptionClick(e, clickedItem, anchor);
    setAnchor(null);
  }

  return (
    <>
      <Button
        icon={props.vertical ? <MoreVertIcon /> : <MoreHorizIcon />}
        onClick={(e) => setAnchor(e.currentTarget)}
      />
      <Menu
        id="card-item-menu"
        MenuListProps={{
          'aria-labelledby': 'card-item-menu',
          sx: { maxWidth: 400, minWidth: 175 },
        }}
        anchorEl={anchor}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={!!anchor}
        onClose={() => setAnchor(null)}
      >
        {sectionedOptions.map((options, idx) => (
          <Box key={idx}>
            {options.map((option, optionIndex) => {
              const optionItem = getOptionItem(option);
              const menuItemProps = getMenuItemProps(option);

              return (
                <MenuItem
                  key={optionItem}
                  divider={(options.length - 1) === optionIndex}
                  sx={{ pt: 1, pb: 1 }}
                  data-testid={optionItem}
                  onClick={(e: MouseEventLI) => handleOptionClick(e, { option: optionItem, item })}
                  {...menuItemProps}
                >
                  {renderIcon(optionItem)}
                  <ListItemText sx={{ ml: 2 }}>{optionItem}</ListItemText>
                </MenuItem>
              );
            })}
          </Box>
        ))}
        {props.children && (props.children)}
      </Menu>
    </>
  );
}
